import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  CURRENCIES,
  Currency,
  FxRates,
  SEED_RATES,
  fetchFxRates,
  formatMad,
  formatPrice,
} from '../lib/currency';

const STORAGE_KEY = 'handcrafted.currency';
const DEFAULT_CURRENCY: Currency = 'MAD';

interface CurrencyContextValue {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  rates: FxRates;
  /** Format a MAD-stored amount in the currently selected currency. */
  format: (amountMad: number) => string;
  /** Format a MAD-stored amount in MAD, ignoring the selected currency. */
  formatMad: (amountMad: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

function readStoredCurrency(): Currency {
  if (typeof window === 'undefined') return DEFAULT_CURRENCY;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (CURRENCIES as readonly string[]).includes(raw)) {
      return raw as Currency;
    }
  } catch {
    // localStorage unavailable — fall through to default.
  }
  return DEFAULT_CURRENCY;
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [currency, setCurrencyState] = useState<Currency>(() => readStoredCurrency());
  const [rates, setRates] = useState<FxRates>(SEED_RATES);

  // Fetch live rates once on mount. fetchFxRates never throws — it falls back
  // to SEED_RATES — so the initial render is always usable.
  useEffect(() => {
    let cancelled = false;
    fetchFxRates().then((result) => {
      if (!cancelled) setRates(result.rates);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage can throw (Safari private mode / quota) — selection still
      // applies for the session via state.
    }
  }, []);

  const format = useCallback(
    (amountMad: number) => formatPrice(amountMad, currency, rates, lang),
    [currency, rates, lang],
  );

  // MAD label follows the UI language ("1 490 MAD" → "1 490 درهم" in Arabic),
  // including the call sites that are always MAD (WhatsApp message, story).
  const formatMadLocalized = useCallback(
    (amountMad: number) => formatMad(amountMad, lang),
    [lang],
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({ currency, setCurrency, rates, format, formatMad: formatMadLocalized }),
    [currency, setCurrency, rates, format, formatMadLocalized],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency must be used inside CurrencyProvider');
  }
  return ctx;
}
